import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner, Offcanvas } from 'react-bootstrap';
import { marketingAgentService } from '../services/apiService';
import { useAuthToken } from '../hooks/useAuthToken';
import { serverMessage } from '../components/PageState';
import { BELOW_DESKTOP_QUERY, useMediaQuery } from '../hooks/useMediaQuery';
import { usePermissions } from '../context/PermissionsContext';
import { useTheme } from '../context/ThemeContext';

const POLL_INTERVAL_MS = 2000;
// A poll may fail now and then (a deploy, a slow network); give up only after this many in a row
const MAX_POLL_FAILURES = 3;
const NAVBAR_HEIGHT = 64;

// Claude-inspired palettes
const palettes = {
  light: {
    bg: '#FAF9F5',
    panel: '#F5F3EC',
    border: '#E8E6DD',
    text: '#3D3929',
    muted: '#8A8674',
    accent: '#D97757',
    green: '#5E8D66',
    red: '#BF4D43',
    bubble: '#F0EEE6',
    diffAdd: '#E7F0E4',
    diffDel: '#F7E5E2',
  },
  dark: {
    bg: '#262624',
    panel: '#21211F',
    border: '#3E3E38',
    text: '#E8E6DD',
    muted: '#A6A39A',
    accent: '#D97757',
    green: '#7BA884',
    red: '#E0796E',
    bubble: '#30302E',
    diffAdd: '#2E3A2C',
    diffDel: '#42302C',
  },
};

const mono = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: '0.8125rem',
};

const PROMPT_TEMPLATES = [
  'Fix any typos on ',
  'Update the copy on ',
  'Change a link on ',
  'Update the page title and meta description of ',
];

// A push deploys the test site only; production is published from the page editor.
const DEPLOY_LABELS = {
  NOT_TRACKED: 'Test site deploy not tracked (set MARKETING_SITE_CIRCLECI_TOKEN)',
  PENDING: 'Test site deploy queued',
  DEPLOYING: 'Deploying to the test site',
  DEPLOYED: 'Deployed to the test site ✓',
  DEPLOY_FAILED: 'Test site deploy failed. Check CircleCI.',
  UNKNOWN: 'Test site deploy status unknown',
};

const DEPLOY_IN_PROGRESS = new Set(['PENDING', 'DEPLOYING']);

/** A run is worth following while Claude is working or its test site deploy is still going. */
const isWorking = (job) => job.status === 'RUNNING' || DEPLOY_IN_PROGRESS.has(job.deployStatus);

/** When the current run started: the latest user message, so a follow-up does not count from the first. */
const runStartedAt = (job) => job.activity?.filter((a) => a.type === 'user').slice(-1)[0]?.timestamp || job.createdAt;

const RUN_STATUS_LABELS = {
  RUNNING: 'Running',
  WAITING_APPROVAL: 'Needs approval',
  COMPLETED: 'Done',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
};

const formatElapsed = (fromIso, toMillis) => {
  const seconds = Math.max(0, Math.floor((toMillis - new Date(fromIso).getTime()) / 1000));
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

const timeAgo = (iso) => {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

const statusColor = (status, c) => {
  switch (status) {
    case 'COMPLETED':
      return c.green;
    case 'WAITING_APPROVAL':
      return c.accent;
    case 'RUNNING':
    case 'CANCELLED':
      return c.muted;
    default:
      return c.red;
  }
};

const truncatePrompt = (text) => (text && text.length > 60 ? `${text.slice(0, 59).trimEnd()}...` : text);

/** "+3 -1" for a unified diff. */
const diffCounts = (patch) => {
  let added = 0;
  let removed = 0;
  for (const line of (patch || '').split('\n')) {
    if (line.startsWith('+') && !line.startsWith('+++')) added += 1;
    else if (line.startsWith('-') && !line.startsWith('---')) removed += 1;
  }
  return { added, removed };
};

const UserBubble = ({ text, c }) => (
  <div className="d-flex justify-content-end mb-3">
    <div
      style={{
        backgroundColor: c.bubble,
        borderRadius: '1rem',
        padding: '0.6rem 1rem',
        maxWidth: '85%',
        whiteSpace: 'pre-wrap',
      }}
    >
      {text}
    </div>
  </div>
);

const FeedEntry = ({ entry, c }) => {
  const time = new Date(entry.timestamp).toLocaleTimeString([], { hour12: false });
  switch (entry.type) {
    case 'user':
      return <UserBubble text={entry.detail} c={c} />;
    case 'assistant':
      return (
        <div className="d-flex mb-3" title={time}>
          <span style={{ color: c.accent, marginRight: '0.6rem', flexShrink: 0 }}>✳</span>
          <span style={{ whiteSpace: 'pre-wrap', color: c.text }}>{entry.detail}</span>
        </div>
      );
    case 'tool':
      return (
        <div className="d-flex mb-2" title={time}>
          <span style={{ color: c.accent, marginRight: '0.6rem', flexShrink: 0 }}>⏺</span>
          <span style={{ ...mono, color: c.muted }}>{entry.detail}</span>
        </div>
      );
    case 'tool_error':
    case 'failed':
      return (
        <div className="d-flex mb-2" title={time}>
          <span style={{ color: c.red, marginRight: '0.6rem', flexShrink: 0 }}>⏺</span>
          <span style={{ ...mono, color: c.red }}>{entry.detail}</span>
        </div>
      );
    case 'commit':
    case 'pushed':
      return (
        <div className="d-flex mb-2" title={time}>
          <span style={{ color: c.green, marginRight: '0.6rem', flexShrink: 0 }}>✓</span>
          <span style={{ ...mono, color: c.green }}>{entry.detail}</span>
        </div>
      );
    case 'review':
      return (
        <div className="d-flex mb-2" title={time}>
          <span style={{ color: c.accent, marginRight: '0.6rem', flexShrink: 0 }}>⏸</span>
          <span style={{ ...mono, color: c.text }}>{entry.detail}</span>
        </div>
      );
    case 'deploy':
      return (
        <div className="d-flex mb-2" title={time}>
          <span style={{ color: c.accent, marginRight: '0.6rem', flexShrink: 0 }}>⏺</span>
          <span style={{ ...mono, color: c.muted }}>{entry.detail}</span>
        </div>
      );
    case 'cancel':
    case 'cancelled':
    case 'done':
      return (
        <div className="d-flex mb-2" title={time}>
          <span style={{ color: c.muted, marginRight: '0.6rem', flexShrink: 0 }}>⏺</span>
          <span style={{ ...mono, color: c.muted }}>{entry.detail}</span>
        </div>
      );
    default:
      // 'start' and 'model' steps are covered by the bubbles + thinking indicator
      return null;
  }
};

const DiffView = ({ patch, c }) => {
  if (!patch) {
    return <div style={{ ...mono, color: c.muted }}>No text diff available.</div>;
  }
  return (
    <pre
      style={{
        ...mono,
        margin: '0.5rem 0 0',
        padding: '0.5rem',
        border: `1px solid ${c.border}`,
        borderRadius: '0.5rem',
        maxHeight: '280px',
        overflow: 'auto',
        backgroundColor: 'transparent',
      }}
    >
      {patch.split('\n').map((line, i) => {
        let color = c.muted;
        let bg = 'transparent';
        if (line.startsWith('+')) {
          color = c.green;
          bg = c.diffAdd;
        } else if (line.startsWith('-')) {
          color = c.red;
          bg = c.diffDel;
        } else if (line.startsWith('@@')) {
          color = c.accent;
        }
        return (
          <div key={i} style={{ color, backgroundColor: bg }}>
            {line || ' '}
          </div>
        );
      })}
    </pre>
  );
};

const FileActions = ({ path, isHtml, onDiff, diffOpen, onPreview, previewOpen, openUrl, c }) => {
  const linkButton = {
    ...mono,
    background: 'none',
    border: 'none',
    padding: 0,
    color: c.accent,
    cursor: 'pointer',
  };
  return (
    <span className="ms-2">
      <button type="button" style={linkButton} onClick={onDiff}>
        {diffOpen ? 'hide diff' : 'diff'}
      </button>
      {isHtml && (
        <>
          {' · '}
          <button type="button" style={linkButton} onClick={onPreview}>
            {previewOpen ? 'hide preview' : 'preview'}
          </button>
          {openUrl && (
            <>
              {' · '}
              <a href={openUrl} target="_blank" rel="noreferrer" style={{ ...mono, color: c.accent }}>
                open ↗
              </a>
            </>
          )}
        </>
      )}
    </span>
  );
};

const iframeStyle = (c) => ({
  width: '100%',
  height: '420px',
  border: `1px solid ${c.border}`,
  borderRadius: '0.5rem',
  marginTop: '0.5rem',
  backgroundColor: '#fff',
});

const CommitBlock = ({ commit, siteUrl, c }) => {
  const [openDiff, setOpenDiff] = useState(null);
  const [previewPath, setPreviewPath] = useState(null);

  const previewUrl = (path) => `${siteUrl}/${path.replace(/^site\//, '')}`;

  return (
    <div
      style={{
        border: `1px solid ${c.border}`,
        borderRadius: '0.75rem',
        padding: '0.75rem 1rem',
        margin: '0.5rem 0 1rem 1.4rem',
      }}
    >
      <a href={commit.url} target="_blank" rel="noreferrer" style={{ ...mono, color: c.accent }}>
        Commit {commit.sha?.slice(0, 7)} →
      </a>
      {commit.files?.map((file) => {
        const diff = commit.diffs?.find((d) => d.path === file);
        return (
          <div key={file} className="mt-1">
            <span style={{ ...mono, color: c.muted }}>{file}</span>
            <FileActions
              path={file}
              isHtml={file.endsWith('.html')}
              onDiff={() => setOpenDiff(openDiff === file ? null : file)}
              diffOpen={openDiff === file}
              onPreview={() => setPreviewPath(previewPath === file ? null : file)}
              previewOpen={previewPath === file}
              openUrl={previewUrl(file)}
              c={c}
            />
            {openDiff === file && <DiffView patch={diff?.patch} c={c} />}
            {previewPath === file && (
              <iframe title={`Preview of ${file}`} src={previewUrl(file)} style={iframeStyle(c)} />
            )}
          </div>
        );
      })}
    </div>
  );
};

const PendingReview = ({ job, canEdit, busy, onApprove, onDiscard, c }) => {
  // The first file's diff starts open, so what is approved has been seen
  const [openDiff, setOpenDiff] = useState(() => job.pendingFiles?.[0]?.path ?? null);
  const [previewPath, setPreviewPath] = useState(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // Point relative CSS/asset URLs in the staged HTML at the live site
  const withBase = (html) =>
    html.replace(/<head([^>]*)>/i, `<head$1><base href="${job.siteUrl}/">`);

  return (
    <div
      style={{
        border: `1px solid ${c.accent}`,
        borderRadius: '0.75rem',
        padding: '0.75rem 1rem',
        margin: '0.5rem 0 1rem 1.4rem',
      }}
    >
      <div style={{ color: c.text }} className="mb-2">
        <strong>Review staged changes</strong>
        <span style={{ color: c.muted }}>. Nothing has been pushed yet.</span>
      </div>
      {job.pendingFiles?.map((file) => {
        const { added, removed } = diffCounts(file.patch);
        return (
          <div key={file.path} className="mt-1">
            <span style={{ ...mono, color: c.muted }}>{file.path}</span>
            <span style={{ ...mono, marginLeft: '0.5rem' }}>
              <span style={{ color: c.green }}>+{added}</span> <span style={{ color: c.red }}>-{removed}</span>
            </span>
            <FileActions
              path={file.path}
              isHtml={file.path.endsWith('.html')}
              onDiff={() => setOpenDiff(openDiff === file.path ? null : file.path)}
              diffOpen={openDiff === file.path}
              onPreview={() => setPreviewPath(previewPath === file.path ? null : file.path)}
              previewOpen={previewPath === file.path}
              openUrl={null}
              c={c}
            />
            {openDiff === file.path && <DiffView patch={file.patch} c={c} />}
            {previewPath === file.path && (
              <iframe
                title={`Staged preview of ${file.path}`}
                srcDoc={withBase(file.content)}
                sandbox="allow-same-origin"
                style={iframeStyle(c)}
              />
            )}
          </div>
        );
      })}
      {canEdit && !confirmDiscard && (
        <div className="d-flex flex-wrap gap-2 mt-3">
          <button type="button" className="msa-btn msa-btn-green" disabled={!!busy} onClick={onApprove}>
            {busy === 'approve' ? 'Pushing to test site' : 'Approve and push to test site'}
          </button>
          <button type="button" className="msa-btn msa-btn-ghost" disabled={!!busy} onClick={() => setConfirmDiscard(true)}>
            Discard
          </button>
        </div>
      )}
      {canEdit && confirmDiscard && (
        <div className="d-flex flex-wrap align-items-center gap-2 mt-3" role="group" aria-label="Confirm discard">
          <span style={{ color: c.text }}>Discard these staged changes? They cannot be recovered.</span>
          <button
            type="button"
            className="msa-btn msa-btn-danger"
            disabled={!!busy}
            onClick={async () => {
              if (!(await onDiscard())) setConfirmDiscard(false);
            }}
          >
            {busy === 'discard' ? 'Discarding' : 'Discard changes'}
          </button>
          <button type="button" className="msa-btn msa-btn-ghost" disabled={!!busy} onClick={() => setConfirmDiscard(false)} autoFocus>
            Keep them
          </button>
        </div>
      )}
    </div>
  );
};

const ConfigDot = ({ ok, label, c }) => (
  <div style={{ fontSize: '0.8125rem' }}>
    <span style={{ color: ok ? c.green : c.red, marginRight: '0.4rem' }}>●</span>
    <span style={{ color: c.muted }}>
      {label} {ok ? '' : '(not configured)'}
    </span>
  </div>
);

/** A failed load in the runs panel, with a retry. */
const RetryLine = ({ text, onRetry, c }) => (
  <div style={{ color: c.red, fontSize: '0.8125rem' }}>
    {text}{' '}
    <button type="button" className="msa-link" onClick={onRetry}>
      Try again
    </button>
  </div>
);

/** New chat, recent runs and usage: the sidebar on tablet and desktop, a slide-out panel on phones. */
const RunsPanel = ({ c, running, history, historyError, job, info, infoError, totalSpend, onNewChat, onOpen, onRetryHistory, onRetryInfo }) => (
  <>
    <div className="p-3" style={{ borderBottom: `1px solid ${c.border}` }}>
      <button type="button" className="msa-btn msa-btn-accent w-100 justify-content-center" onClick={onNewChat} disabled={running}>
        + New chat
      </button>
    </div>

    {/* Recent runs (scrollable) */}
    <div className="flex-grow-1" style={{ overflowY: 'auto', padding: '0.75rem' }}>
      <div style={{ color: c.muted, fontSize: '0.75rem' }} className="mb-2 text-uppercase">
        Recent runs
      </div>
      {historyError && <RetryLine text="Could not load the recent runs." onRetry={onRetryHistory} c={c} />}
      {!historyError && history.length === 0 && (
        <div style={{ color: c.muted, fontSize: '0.8125rem' }}>No runs yet.</div>
      )}
      {history.map((item) => (
        <button
          key={item.id}
          type="button"
          className="msa-run-row"
          aria-current={job?.id === item.id ? 'true' : undefined}
          onClick={() => onOpen(item.id)}
        >
          <span className="d-flex align-items-baseline">
            <span
              className={item.status === 'RUNNING' ? 'claude-thinking' : undefined}
              style={{ color: statusColor(item.status, c), marginRight: '0.5rem' }}
              aria-hidden="true"
            >
              ●
            </span>
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '0.875rem',
              }}
            >
              {item.prompt}
            </span>
          </span>
          <span className="d-block" style={{ color: c.muted, fontSize: '0.75rem', paddingLeft: '1.1rem' }}>
            <span style={{ color: statusColor(item.status, c) }}>{RUN_STATUS_LABELS[item.status] || item.status}</span>
            {' · '}
            {timeAgo(item.createdAt)} · ${item.estimatedCostUsd.toFixed(2)}
            {item.commitCount > 0 && ` · ${item.commitCount} commit${item.commitCount > 1 ? 's' : ''}`}
          </span>
        </button>
      ))}
    </div>

    {/* Useful info */}
    <div className="p-3" style={{ borderTop: `1px solid ${c.border}`, fontSize: '0.8125rem' }}>
      <div style={{ color: c.muted, fontSize: '0.75rem' }} className="mb-2 text-uppercase">
        Usage
      </div>
      <div className="d-flex mb-1">
        <span style={{ color: c.muted }}>Est. spend (last {history.length} runs)</span>
        <span className="ms-auto" style={{ color: c.text }}>
          ${totalSpend.toFixed(2)}
        </span>
      </div>
      <div className="mb-3">
        <a
          href="https://console.anthropic.com/settings/billing"
          target="_blank"
          rel="noreferrer"
          style={{ color: c.accent, textDecoration: 'none' }}
        >
          Credit balance (Anthropic console) ↗
        </a>
      </div>

      <div style={{ color: c.muted, fontSize: '0.75rem' }} className="mb-2 text-uppercase">
        Target
      </div>
      {info ? (
        <>
          <div className="mb-1">
            <a href={info.repoUrl} target="_blank" rel="noreferrer" style={{ color: c.accent, textDecoration: 'none' }}>
              {info.repo} ↗
            </a>
            <span style={{ color: c.muted }}> · {info.branch}</span>
          </div>
          <div className="mb-3">
            <a href={info.siteUrl} target="_blank" rel="noreferrer" style={{ color: c.accent, textDecoration: 'none' }}>
              {info.siteUrl.replace('https://', '')} ↗
            </a>
          </div>
          <ConfigDot ok={info.anthropicConfigured} label="Anthropic API" c={c} />
          <ConfigDot ok={info.githubConfigured} label="GitHub token" c={c} />
          <ConfigDot ok={info.circleciConfigured} label="CircleCI (deploy status)" c={c} />
        </>
      ) : infoError ? (
        <RetryLine text="Could not load the settings." onRetry={onRetryInfo} c={c} />
      ) : (
        <div style={{ color: c.muted }}>Loading</div>
      )}
    </div>
  </>
);

const MarketingSite = () => {
  const ensureToken = useAuthToken();
  const { canEdit } = usePermissions();
  const { theme } = useTheme();
  const c = palettes[theme === 'dark' ? 'dark' : 'light'];

  const [input, setInput] = useState('');
  // One busy state for the review buttons, so Approve and Discard can never both be in flight
  const [reviewBusy, setReviewBusy] = useState(null); // null | 'approve' | 'discard'
  const [job, setJob] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyError, setHistoryError] = useState(false);
  const [info, setInfo] = useState(null);
  const [infoError, setInfoError] = useState(false);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [showRuns, setShowRuns] = useState(false);
  const pollRef = useRef(null); // { timer } of the poll that is following a run
  const openSeq = useRef(0);
  const logRef = useRef(null);
  const composerRef = useRef(null);

  const running = job?.status === 'RUNNING';
  const deploying = job?.deployStatus === 'PENDING' || job?.deployStatus === 'DEPLOYING';

  const loadHistory = useCallback(async () => {
    try {
      await ensureToken();
      const response = await marketingAgentService.listJobs();
      setHistory(response.data);
      setHistoryError(false);
    } catch {
      // Shown in the runs panel with a retry; the chat still works
      setHistoryError(true);
    }
  }, [ensureToken]);

  const loadInfo = useCallback(async () => {
    setInfoError(false);
    try {
      await ensureToken();
      const response = await marketingAgentService.getInfo();
      setInfo(response.data);
    } catch {
      setInfoError(true);
    }
  }, [ensureToken]);

  useEffect(() => {
    loadHistory();
    loadInfo();
  }, [loadHistory, loadInfo]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearTimeout(pollRef.current.timer);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  // Tick the elapsed-time display every second while a run is in progress
  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [running]);

  // Keep the conversation scrolled to the newest entry
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [job?.activity?.length, job?.commits?.length, job?.status]);

  /**
   * Follows a run until Claude has finished and its test site deploy is done. Each poll refreshes
   * the sign-in token (a run can outlast it) and waits for the previous answer, so polls never overlap.
   */
  const pollJob = useCallback(
    (jobId) => {
      stopPolling();
      const poll = { timer: null, failures: 0 };
      pollRef.current = poll;
      const tick = async () => {
        try {
          await ensureToken();
          const response = await marketingAgentService.getJob(jobId);
          if (pollRef.current !== poll) return; // another run was opened meanwhile
          poll.failures = 0;
          setJob(response.data);
          if (!isWorking(response.data)) {
            pollRef.current = null;
            loadHistory();
            return;
          }
        } catch (err) {
          if (pollRef.current !== poll) return;
          poll.failures += 1;
          const gone = err.response?.status === 404;
          if (gone || poll.failures >= MAX_POLL_FAILURES) {
            pollRef.current = null;
            setError(
              gone
                ? 'This run is no longer on the server (the admin app restarted). Its pushed commits are on GitHub.'
                : `Lost track of the run: ${serverMessage(err, err.message)}. Open it again from Recent runs.`
            );
            return;
          }
        }
        poll.timer = setTimeout(tick, POLL_INTERVAL_MS);
      };
      poll.timer = setTimeout(tick, POLL_INTERVAL_MS);
    },
    [ensureToken, loadHistory, stopPolling]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || running) return;
    setError(null);
    try {
      await ensureToken();
      const response =
        job && job.status !== 'RUNNING'
          ? await marketingAgentService.reply(job.id, text)
          : await marketingAgentService.startJob(text);
      setJob(response.data);
      setInput('');
      pollJob(response.data.id);
      loadHistory();
    } catch (err) {
      setError(serverMessage(err, err.message));
    }
  };

  const handleApprove = async () => {
    if (reviewBusy) return;
    setError(null);
    setReviewBusy('approve');
    try {
      await ensureToken();
      const response = await marketingAgentService.approveJob(job.id);
      setJob(response.data);
      pollJob(job.id); // follow the test site deploy
      loadHistory();
    } catch (err) {
      setError(serverMessage(err, err.message));
    } finally {
      setReviewBusy(null);
    }
  };

  /** Returns whether the staged changes were discarded. */
  const handleDiscard = async () => {
    if (reviewBusy) return false;
    setError(null);
    setReviewBusy('discard');
    try {
      await ensureToken();
      const response = await marketingAgentService.discardJob(job.id);
      setJob(response.data);
      loadHistory();
      return true;
    } catch (err) {
      setError(serverMessage(err, err.message));
      return false;
    } finally {
      setReviewBusy(null);
    }
  };

  const handleCancel = async () => {
    try {
      await ensureToken();
      await marketingAgentService.cancelJob(job.id);
    } catch (err) {
      setError(serverMessage(err, err.message));
    }
  };

  const handleNewChat = () => {
    openSeq.current += 1;
    stopPolling();
    setJob(null);
    setInput('');
    setError(null);
  };

  const openHistoryJob = async (id) => {
    const seq = ++openSeq.current;
    setError(null);
    try {
      await ensureToken();
      const response = await marketingAgentService.getJob(id);
      if (seq !== openSeq.current) return; // a later click won
      setJob(response.data);
      // Keep following a run that is still working, or whose test site deploy is still going
      if (isWorking(response.data)) {
        pollJob(id);
      } else {
        stopPolling();
      }
    } catch (err) {
      if (seq === openSeq.current) setError(serverMessage(err, err.message));
    }
  };

  const applyTemplate = (template) => {
    setInput(template);
    requestAnimationFrame(() => {
      const el = composerRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(template.length, template.length);
    });
  };

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  const lastModelStep = job?.activity?.filter((a) => a.type === 'model').slice(-1)[0];

  const statusLabel = !job
    ? 'Ready'
    : running
      ? `Working · ${formatElapsed(runStartedAt(job), now)}`
      : job.status === 'WAITING_APPROVAL'
        ? 'Needs your approval'
        : job.status === 'COMPLETED'
          ? 'Done'
          : job.status === 'CANCELLED'
            ? 'Cancelled'
            : 'Failed';

  const totalSpend = history.reduce((sum, item) => sum + (item.estimatedCostUsd || 0), 0);
  const waitingHere = job?.status === 'WAITING_APPROVAL' && job.pendingFiles?.length > 0;
  const waitingElsewhere = !waitingHere && history.find((h) => h.status === 'WAITING_APPROVAL' && h.id !== job?.id);
  const pushed = job?.commits?.length > 0;

  // Below desktop the nav is taller and phone browsers' toolbars come and go: fill the space that is
  // actually left (dynamic viewport height) so the composer never sits under the browser's toolbar.
  const belowDesktop = useMediaQuery(BELOW_DESKTOP_QUERY);
  const [navHeight, setNavHeight] = useState(NAVBAR_HEIGHT);
  useLayoutEffect(() => {
    const measure = () => {
      const nav = document.querySelector('.navbar-custom');
      if (nav) setNavHeight(nav.offsetHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  return (
    <div
      className="d-flex"
      style={{
        height: belowDesktop ? `calc(100dvh - ${navHeight}px)` : `calc(100vh - ${NAVBAR_HEIGHT}px)`,
        overflow: 'hidden',
        backgroundColor: c.bg,
        color: c.text,
      }}
    >
      {/* Page-scoped styles in the page's own palette, so Clarity's buttons and focus ring stay out */}
      <style>{`
        @keyframes claude-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.35; }
        }
        .claude-thinking { animation: claude-pulse 1.6s ease-in-out infinite; }
        .msa-run-row {
          display: block; width: 100%; text-align: left; border: 0; background: transparent; color: inherit;
          padding: 0.5rem 0.6rem; border-radius: 0.5rem;
        }
        .msa-run-row:hover, .msa-run-row[aria-current] { background-color: ${c.bubble}; }
        .msa-btn {
          display: inline-flex; align-items: center; gap: 0.35rem; padding: 0.3rem 0.8rem; font-size: 0.875rem;
          line-height: 1.4; border: 1px solid transparent; border-radius: 0.5rem; background: transparent; color: inherit;
        }
        .msa-btn:disabled { opacity: 0.55; cursor: not-allowed; }
        .msa-btn:not(:disabled):hover { filter: brightness(0.95); }
        .msa-btn-accent { background-color: ${c.accent}; border-color: ${c.accent}; color: #fff; }
        .msa-btn-green { background-color: ${c.green}; border-color: ${c.green}; color: #fff; }
        .msa-btn-ghost { border-color: ${c.border}; color: ${c.muted}; }
        .msa-btn-ghost:not(:disabled):hover { background-color: ${c.bubble}; color: ${c.text}; filter: none; }
        .msa-btn-danger { border-color: ${c.red}; color: ${c.red}; }
        .msa-chip { border-radius: 1rem; font-size: 0.8125rem; }
        .msa-link { border: 0; background: none; padding: 0; color: ${c.accent}; font: inherit; text-decoration: underline; }
        .msa-composer {
          flex: 1; min-width: 0; resize: none; padding: 0.5rem 0.75rem; font-size: 0.875rem;
          background-color: ${c.bubble}; color: ${c.text}; border: 1px solid ${c.border}; border-radius: 0.75rem;
        }
        .msa-composer::placeholder { color: ${c.muted}; }
        .msa-composer:disabled { opacity: 0.7; }
        .msa-btn:focus-visible, .msa-run-row:focus-visible, .msa-link:focus-visible, .msa-composer:focus {
          outline: none; border-color: ${c.accent}; box-shadow: 0 0 0 0.2rem ${c.accent}40;
        }
      `}</style>

      {/* ── Sidebar ─────────────────────────────────────── */}
      <div
        className="d-none d-md-flex flex-column flex-shrink-0"
        style={{ width: '300px', borderRight: `1px solid ${c.border}`, backgroundColor: c.panel }}
      >
        <RunsPanel
          c={c}
          running={running}
          history={history}
          historyError={historyError}
          job={job}
          info={info}
          infoError={infoError}
          totalSpend={totalSpend}
          onNewChat={handleNewChat}
          onOpen={openHistoryJob}
          onRetryHistory={loadHistory}
          onRetryInfo={loadInfo}
        />
      </div>

      {/* Phones: the same panel slides out from the header's Runs button */}
      <Offcanvas
        show={showRuns}
        onHide={() => setShowRuns(false)}
        placement="start"
        className="d-md-none"
        style={{ width: '300px', backgroundColor: c.panel, color: c.text }}
        aria-labelledby="msa-runs-title"
      >
        <div className="d-flex align-items-center px-3 py-2" style={{ borderBottom: `1px solid ${c.border}` }}>
          <strong id="msa-runs-title">Runs</strong>
          <button type="button" className="msa-btn msa-btn-ghost ms-auto" onClick={() => setShowRuns(false)}>
            Close
          </button>
        </div>
        <RunsPanel
          c={c}
          running={running}
          history={history}
          historyError={historyError}
          job={job}
          info={info}
          infoError={infoError}
          totalSpend={totalSpend}
          onNewChat={() => {
            handleNewChat();
            setShowRuns(false);
          }}
          onOpen={(id) => {
            openHistoryJob(id);
            setShowRuns(false);
          }}
          onRetryHistory={loadHistory}
          onRetryInfo={loadInfo}
        />
      </Offcanvas>

      {/* ── Main chat panel ─────────────────────────────── */}
      <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0 }}>
        {/* Header */}
        <div
          className="d-flex flex-wrap align-items-center column-gap-3 row-gap-1 px-4 py-2"
          style={{ borderBottom: `1px solid ${c.border}` }}
        >
          <strong>Marketing site agent</strong>
          <span className="d-none d-md-inline" style={{ color: c.muted, fontSize: '0.8125rem' }}>
            Changes are staged for your approval, then pushed to {info?.branch || 'main'}, which deploys the test
            site. Deploy to production from the{' '}
            <Link to="/marketing-site" style={{ color: c.accent }}>
              page editor
            </Link>
            .
          </span>
          <button
            type="button"
            className="d-md-none ms-auto"
            onClick={() => setShowRuns(true)}
            style={{
              background: 'none',
              border: `1px solid ${c.border}`,
              borderRadius: '0.5rem',
              color: c.accent,
              padding: '0.25rem 0.75rem',
              fontSize: '0.875rem',
            }}
          >
            Runs and new chat
          </button>
          <span className="d-md-none w-100" style={{ color: c.muted, fontSize: '0.75rem' }}>
            Approved changes go to the test site. Deploy to production from the{' '}
            <Link to="/marketing-site" style={{ color: c.accent }}>
              page editor
            </Link>
            .
          </span>
        </div>

        {/* Conversation (scrollable) */}
        <div ref={logRef} className="flex-grow-1" style={{ overflowY: 'auto' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto', padding: '1.25rem 1.5rem' }}>
            {!job && (
              <div className="text-center" style={{ paddingTop: '18vh' }}>
                <div style={{ color: c.accent, fontSize: '1.75rem' }}>✳</div>
                <div style={{ color: c.muted }} className="mt-2 mb-3">
                  Describe a change to the marketing website. Claude will edit the site and stage
                  the changes for your review.
                </div>
                {canEdit && (
                  <div>
                    {PROMPT_TEMPLATES.map((template) => (
                      <button
                        key={template}
                        type="button"
                        className="msa-btn msa-btn-ghost msa-chip me-2 mb-2"
                        onClick={() => applyTemplate(template)}
                      >
                        {template.trim()}...
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {job && (
              <>
                <UserBubble text={job.prompt} c={c} />
                {job.activity?.map((entry, idx) => (
                  <FeedEntry key={idx} entry={entry} c={c} />
                ))}

                {job.commits?.map((commit) => (
                  <CommitBlock key={commit.sha} commit={commit} siteUrl={job.siteUrl} c={c} />
                ))}

                {waitingHere && (
                  <PendingReview
                    key={job.id}
                    job={job}
                    canEdit={canEdit}
                    busy={reviewBusy}
                    onApprove={handleApprove}
                    onDiscard={handleDiscard}
                    c={c}
                  />
                )}

                {running && (
                  <div className="d-flex claude-thinking mb-2" style={{ color: c.accent }}>
                    <span style={{ marginRight: '0.6rem' }}>✳</span>
                    <span>
                      {lastModelStep ? lastModelStep.detail.replace('...', '') : 'Starting'}…
                    </span>
                  </div>
                )}

                {job.status === 'FAILED' && job.error && (
                  <div className="d-flex mb-2">
                    <span style={{ color: c.red, marginRight: '0.6rem' }}>✗</span>
                    <span style={{ color: c.red }}>{job.error}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Input pinned at the bottom */}
        <div style={{ borderTop: `1px solid ${c.border}` }}>
          <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0.75rem 1.5rem 0.4rem' }}>
            {/* Errors sit next to the buttons that caused them, outside the scrolling log */}
            {error && (
              <div
                role="alert"
                className="d-flex align-items-start mb-2"
                style={{
                  color: c.red,
                  border: `1px solid ${c.red}`,
                  borderRadius: '0.5rem',
                  padding: '0.4rem 0.75rem',
                  fontSize: '0.875rem',
                }}
              >
                <span style={{ marginRight: '0.5rem' }} aria-hidden="true">
                  ✗
                </span>
                <span className="flex-grow-1" style={{ whiteSpace: 'pre-wrap', minWidth: 0 }}>
                  {error}
                </span>
                <button type="button" className="msa-link ms-2" onClick={() => setError(null)}>
                  Dismiss
                </button>
              </div>
            )}
            {canEdit && waitingHere && (
              <div className="d-flex mb-2" style={{ fontSize: '0.875rem' }} role="status">
                <span style={{ color: c.accent, marginRight: '0.5rem' }} aria-hidden="true">
                  ⏸
                </span>
                <span style={{ color: c.text }}>
                  Needs your approval. Review the staged changes above, then approve or discard them, or describe
                  what to change.
                </span>
              </div>
            )}
            {canEdit && waitingElsewhere && (
              <div className="d-flex mb-2" style={{ fontSize: '0.875rem' }} role="status">
                <span style={{ color: c.accent, marginRight: '0.5rem' }} aria-hidden="true">
                  ⏸
                </span>
                <span style={{ color: c.text }}>
                  Another run needs your approval: &quot;{truncatePrompt(waitingElsewhere.prompt)}&quot;.{' '}
                  <button type="button" className="msa-link" onClick={() => openHistoryJob(waitingElsewhere.id)}>
                    Open it
                  </button>
                </span>
              </div>
            )}
            {canEdit ? (
              <form onSubmit={handleSubmit} className="d-flex align-items-end">
                <textarea
                  ref={composerRef}
                  className="msa-composer"
                  rows={2}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  aria-label={job ? 'Message to Claude' : 'Describe a change'}
                  placeholder={
                    running
                      ? 'Waiting for the current run to finish'
                      : waitingHere
                        ? 'Request changes to the staged edits, or approve or discard them above'
                        : job
                          ? 'Follow up, for example "now do the same on the pricing page" (Cmd+Enter to send)'
                          : 'Describe a change (Cmd+Enter to run)'
                  }
                  disabled={running}
                />
                {running ? (
                  <button type="button" className="msa-btn msa-btn-danger ms-2" onClick={handleCancel}>
                    Cancel
                  </button>
                ) : (
                  <button type="submit" className="msa-btn msa-btn-accent ms-2" disabled={!input.trim()}>
                    {job ? 'Send' : 'Run'}
                  </button>
                )}
              </form>
            ) : (
              <div style={{ color: c.muted, fontSize: '0.875rem' }} className="py-1">
                View-only access. Running the marketing site agent needs the ADMIN permission level.
              </div>
            )}

            {/* Status line */}
            <div
              className="d-flex flex-wrap column-gap-3"
              style={{ color: c.muted, fontSize: '0.8125rem', padding: '0.4rem 0' }}
            >
              <span className="d-inline-flex align-items-center">
                {running && <Spinner animation="border" size="sm" className="me-2" />}
                {statusLabel}
              </span>
              {job && job.deployStatus !== 'NONE' && (
                <span className={deploying ? 'claude-thinking' : undefined}>
                  {DEPLOY_LABELS[job.deployStatus] || job.deployStatus}
                </span>
              )}
              {pushed && !deploying && (
                <Link to="/marketing-site" style={{ color: c.accent }}>
                  Open the editor to deploy to production
                </Link>
              )}
              {job?.inputTokens > 0 && (
                <span className="ms-auto">
                  {job.inputTokens.toLocaleString()} in / {job.outputTokens.toLocaleString()} out ·
                  est. ${job.estimatedCostUsd.toFixed(2)}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingSite;
